import { setAnalyticsContext } from "@/analytics/analytics";
import { env } from "@/config/env";
import type { ResolvedEmbedMount } from "@/embed/resolveEmbedMount";
import type { AppMode, IntakeRuntimeConfig, UtmParams } from "@/types/embed";
import { mergeUtm, parseUtmFromSearch } from "@/utils/utm";

interface RuntimeOverrides {
  campaignId?: string;
  source?: string;
  apiBaseUrl?: string;
  appBaseUrl?: string;
  utm?: UtmParams;
}

function applyAnalyticsContext(runtime: IntakeRuntimeConfig): void {
  setAnalyticsContext({
    root: runtime.root,
    offerId: runtime.offerId ?? (runtime.root === "hub" ? "hub" : undefined),
    mode: runtime.mode,
    environment: env.environment,
    campaignId: runtime.campaignId,
    source: runtime.source,
    ...runtime.utm,
  });
}

/** Build runtime for standalone or embed deep-link (single offer). */
export function buildRuntimeConfig(
  offerId: string,
  mode: AppMode,
  overrides: RuntimeOverrides = {},
): IntakeRuntimeConfig {
  const urlUtm = parseUtmFromSearch();
  const utm = overrides.utm ? mergeUtm(urlUtm, overrides.utm) : urlUtm;

  const runtime: IntakeRuntimeConfig = {
    root: "offer",
    offerId,
    campaignId: overrides.campaignId,
    source: overrides.source,
    mode,
    utm,
    apiBaseUrl: overrides.apiBaseUrl ?? env.apiBaseUrl,
    appBaseUrl: overrides.appBaseUrl,
  };

  applyAnalyticsContext(runtime);
  return runtime;
}

/** Build runtime from embed mount resolution (`data-root` / `data-offer`). */
export function buildEmbedRuntimeConfig(
  resolved: ResolvedEmbedMount,
  overrides: RuntimeOverrides = {},
): IntakeRuntimeConfig {
  const urlUtm = parseUtmFromSearch();
  const utm = overrides.utm ? mergeUtm(urlUtm, overrides.utm) : urlUtm;

  const runtime: IntakeRuntimeConfig = {
    root: resolved.root,
    offerId: resolved.offerId,
    campaignId: overrides.campaignId,
    source: overrides.source,
    mode: "embedded",
    utm,
    apiBaseUrl: overrides.apiBaseUrl ?? env.apiBaseUrl,
    appBaseUrl: overrides.appBaseUrl,
  };

  applyAnalyticsContext(runtime);
  return runtime;
}
